/**
 * Source : https://github.com/costidotdev/shadcn-number-input
 */

import { forwardRef, useEffect, useState, useRef } from "react";
import { NumericFormat, NumericFormatProps } from "react-number-format";
import { Input, InputProps } from "./input";
import i18n, {
  getDecimalSeparator,
  getThousandSeparator,
  SupportedLocale,
} from "@/i18n";

export interface NumberInputProps
  extends Omit<NumericFormatProps, "value" | "onValueChange" | "type">,
    Omit<InputProps, "value" | "onValueChange" | "type"> {
  stepper?: number;
  thousandSeparator?: string;
  placeholder?: string;
  defaultValue?: number;
  min?: number;
  max?: number;
  value?: number; // Controlled value
  suffix?: string;
  prefix?: string;
  onValueChange?: (value: number | undefined) => void;
  fixedDecimalScale?: boolean;
  decimalScale?: number;
}

export const NumericInput = forwardRef<HTMLInputElement, NumberInputProps>(
  (
    {
      placeholder,
      defaultValue,
      min = -Infinity,
      max = Infinity,
      onValueChange,
      fixedDecimalScale = false,
      suffix,
      prefix,
      value: controlledValue,
      thousandSeparator = getThousandSeparator(
        i18n.language as SupportedLocale,
      ),
      decimalSeparator = getDecimalSeparator(i18n.language as SupportedLocale),
      ...props
    },
    ref,
  ) => {
    const internalRef = useRef<HTMLInputElement>(null); // Create an internal ref
    const combinedRef = ref || internalRef; // Use provided ref or internal ref
    const [value, setValue] = useState<number | undefined>(
      controlledValue ?? defaultValue,
    );

    useEffect(() => {
      if (controlledValue !== undefined) {
        setValue(controlledValue);
      }
    }, [controlledValue]);

    const handleChange = (values: {
      value: string;
      floatValue: number | undefined;
    }) => {
      const newValue =
        values.floatValue === undefined ? undefined : values.floatValue;
      setValue(newValue);
      if (onValueChange) {
        onValueChange(newValue);
      }
    };

    return (
      <NumericFormat
        value={value}
        onValueChange={handleChange}
        thousandSeparator={thousandSeparator}
        fixedDecimalScale={fixedDecimalScale}
        decimalSeparator={decimalSeparator}
        allowNegative={min < 0}
        valueIsNumericString
        type={"text"}
        max={max}
        min={min}
        suffix={suffix}
        prefix={prefix}
        customInput={Input}
        placeholder={placeholder}
        className="[appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none rounded-r-none relative"
        getInputRef={combinedRef} // Use combined ref
        {...props}
      />
    );
  },
);

NumericInput.displayName = "NumericInput";
